import { UtilityMethods } from "@mindmemobile/mm-shared-global";
import { BillingMethodType, PaymentTransactionStatus } from "../enums";
import {
  IAPIPaymentDetailModel,
  IAPIPaymentDetailPageModel,
  IAPIRelatedPaymentRefundModel,
} from "../interfaces";
import { OrderRelatedGridModel } from "./OrderRelatedGridModels";
import { PaymentMethodModel } from "./PaymentMethodModels";
import { RelatedInvoicesGridModel } from "./RelatedInvoicesGridModel";
import { RelatedSubscriptionGridModel } from "./RelatedSubscriptionGridModel";
import { MerchantAccount } from "./MerchantApplicationInputModel";
import { ItemDetailInputModel } from "./ItemDetailInputModel";
import { DonationPricingPageInputsModel } from "./DonationAndItemModel";
import { ContactReferenceModel } from "./ContactDetailPageModel"; 

export class PaymentDetailPageModel {
  detail: PaymentDetailModel;
  subscriptions: RelatedSubscriptionGridModel[];
  invoices: RelatedInvoicesGridModel[];
  orders: OrderRelatedGridModel[];
  refunds: RelatedRefundsGridModel[];
  totalPayments: number;
  previousRecordId: string;
  nextRecordId: string;
  index: number;
  constructor(data: Partial<PaymentDetailPageModel>) {
    this.detail =
      (data.detail && new PaymentDetailModel(data.detail)) ||
      new PaymentDetailModel({});
    this.subscriptions = data.subscriptions || [];
    this.invoices = data.invoices || [];
    this.refunds = data.refunds || [];
    this.totalPayments = data.totalPayments || 0;
    this.previousRecordId = data.previousRecordId || "";
    this.nextRecordId = data.nextRecordId || "";
    this.index = data.index || 0;
    this.orders =
      data.orders?.map((item) => {
        return new OrderRelatedGridModel(item);
      }) || [];
  }

  static deserializeAPIModel(
    data: IAPIPaymentDetailPageModel
  ): PaymentDetailPageModel {
    if (!data) return new PaymentDetailPageModel({});
    const subscription =
      data.subscription &&
      RelatedSubscriptionGridModel.deserializeAPIModel(data.subscription);
    const mappedData: Partial<PaymentDetailPageModel> = {
      detail: PaymentDetailModel.deserializeAPIModel(data.detail),
      subscriptions: subscription ? [subscription] : [],
      invoices:
        data.invoices?.map((item) =>
          RelatedInvoicesGridModel.deserializeAPIModel(item)
        ) || [],
      orders:
        data.orders?.map((item) =>
          OrderRelatedGridModel.deserializeAPIModel(item)
        ) || [],
      refunds:
        data.refunds?.map((item) =>
          RelatedRefundsGridModel.deserializeAPIModel(item)
        ) || [],
      totalPayments: data.total,
      previousRecordId: data.previousRecordId,
      nextRecordId: data.nextRecordId,
      index: data.index,
    };
    return new PaymentDetailPageModel(mappedData);
  }
}

export class RelatedRefundsGridModel {
  id: string;
  amount: number;
  createdDate: Date;
  isRefund: boolean;
  success: boolean;
  description: string;
  status: keyof typeof PaymentTransactionStatus;
  totalRefunded: number;
  paymentMethod: PaymentMethodModel;
  constructor(data: Partial<RelatedRefundsGridModel>) {
    this.id = data.id || "";
    this.amount = data.amount || 0;
    this.createdDate = data.createdDate || new Date();
    this.isRefund = data.isRefund == true;
    this.success = data.success == true;
    this.description = data.description || "";
    this.status = data.status || "Failed";
    this.totalRefunded = data.totalRefunded || 0;
    this.paymentMethod = data.paymentMethod || new PaymentMethodModel();
  }

  static deserializeAPIModel(
    data: IAPIRelatedPaymentRefundModel
  ): RelatedRefundsGridModel {
    const mappedData: Partial<RelatedRefundsGridModel> = {
      id: data.id,
      amount: data.amount,
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      isRefund: data.isRefund,
      success: data.success,
      description: data.description,
      status: data.status,
      totalRefunded: data.totalRefunded,
      paymentMethod: PaymentMethodModel.deserializeAPIModel(data.paymentMethod),
    };
    return new RelatedRefundsGridModel(mappedData);
  }
}
export class PaymentDetailModel {
  id: string;
  success: boolean;
  status: keyof typeof PaymentTransactionStatus;
  createdDate: Date;
  grossAmount: number;
  netAmount: number;
  /** The gateway's own cut, taken from the merchant's money. */
  processingFee: number;
  /** The processing fee the customer agreed to cover - already part of the amount charged. */
  surcharge: number;
  coverProcessingCost: boolean;
  description: string;
  contact: ContactReferenceModel;
  paymentMethod: PaymentMethodModel;
  paymentMethodId: string;
  subscriptionId: string;
  subscriptionName: string;
  invoiceNumber: string;
  dueDate: Date;
  billingMethod: keyof typeof BillingMethodType;
  merchantAccount?: MerchantAccount;
  invoiceId: string;
  refundAmount: number;
  refundDescription: string;
  statementDescriptor: string;
  /** Whether "Email payment receipt" was ticked, so the edit form can show the saved choice. */
  emailReceipt: boolean;
  showVoidButton: boolean;
  donationDetailPage: ItemDetailInputModel | null;
  donationPricingPageData: DonationPricingPageInputsModel | null;
  constructor(data: Partial<PaymentDetailModel>) {
    this.id = data.id || "";
    this.success = data.success == true;
    this.createdDate = data.createdDate || new Date();
    this.dueDate = data.dueDate || new Date();
    this.grossAmount = data.grossAmount || 0;
    this.netAmount = data.netAmount || 0;
    this.processingFee = data.processingFee || 0;
    this.surcharge = data.surcharge || 0;
    this.coverProcessingCost = data.coverProcessingCost || false;
    this.description = data.description || "";
    this.contact = data.contact || new ContactReferenceModel();
    this.paymentMethod = data.paymentMethod || new PaymentMethodModel();
    this.paymentMethodId = data.paymentMethodId || "";
    this.subscriptionId = data.subscriptionId || "";
    this.status = data.status || "Failed";
    this.billingMethod = data.billingMethod || "ChargePayment";
    this.merchantAccount =
      data.merchantAccount && new MerchantAccount(data.merchantAccount);
    this.subscriptionName = data.subscriptionName || "";
    this.invoiceNumber = data.invoiceNumber || "";
    this.invoiceId = data.invoiceId || "";
    this.refundAmount = data.refundAmount || 0;
    this.refundDescription = data.refundDescription || "";
    this.statementDescriptor = data.statementDescriptor || "";
    this.emailReceipt = data.emailReceipt === true;
    this.showVoidButton = data.showVoidButton === true;
    this.donationDetailPage = data?.donationDetailPage
      ? new ItemDetailInputModel(data?.donationDetailPage)
      : null;
    this.donationPricingPageData = data?.donationPricingPageData
      ? new DonationPricingPageInputsModel(data?.donationPricingPageData)
      : null;
  }

  static deserializeAPIModel(data: IAPIPaymentDetailModel): PaymentDetailModel {
    const mappedData: Partial<PaymentDetailModel> = {
      id: data.id,
      success: data.isPaid,
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      grossAmount: data.grossAmount,
      netAmount: data.netAmount,
      processingFee: data.processingFee,
      surcharge: data.surcharge,
      coverProcessingCost: data.coverProcessingCost,
      description: data.description,
      contact: ContactReferenceModel.deserializeAPIModel(data.contact),
      paymentMethod: PaymentMethodModel.deserializeAPIModel(data.paymentMethod),
      paymentMethodId: data.paymentMethodId,
      subscriptionId: data.subscriptionId,
      status: data.status,
      dueDate: UtilityMethods.removeZFromDateString(data.dueDate),
      billingMethod: data.billingMethod,
      merchantAccount:
        data.merchantAccount &&
        MerchantAccount.deserializeAPIModel(data.merchantAccount),
      subscriptionName: data.subscriptionName,
      emailReceipt: data.emailReceipt === true,
      invoiceNumber: data.invoiceNumber,
      invoiceId: data.invoiceId,
      refundAmount: data.totalRefund,
      statementDescriptor: data.statementDescriptor,
      showVoidButton: data.showVoidButton,
      donationPricingPageData:
        data.donationDetails &&
        DonationPricingPageInputsModel.deserializeAPIModelForPayment(
          data.donationDetails
        ),
    };
    return new PaymentDetailModel(mappedData);
  }
}
